import {
  AlignHorizontalLeft,
  AlignHorizontalCenter,
  AlignHorizontalRight,
  AlignVerticalTop,
  AlignVerticalCenter,
  AlignVerticalBottom,
} from '@mui/icons-material';
import { Box, Typography, ButtonGroup } from '@mui/material';
import React from 'react';
import FBOButton from 'ui/theme/components/FBOButton/FBOButton';
import { LabelElement } from '../../../types';
import {
  getLeftOffset,
  getRightExtent,
  getTopOffset,
  getBottomExtent,
  normalizeDegreeAngleRange,
  getRelativeCenter,
} from '../../../helpers/rotationHelpers';

interface MultipleSelectedElementsPropertiesProps {
  selectedElements: LabelElement[];
  onUpdateMultipleElements: (elements: LabelElement[]) => void;
}

const getRotationMetric = (
  angle: number | undefined,
  width: number,
  height: number,
  dir: 'left' | 'right' | 'top' | 'bottom'
) => {
  const a = angle ? normalizeDegreeAngleRange(angle) : 0;
  switch (dir) {
    case 'left':
      return getLeftOffset(a, width, height);
    case 'right':
      return getRightExtent(a, width, height);
    case 'top':
      return getTopOffset(a, width, height);
    case 'bottom':
      return getBottomExtent(a, width, height);
  }
};

const calculateBoundingBoxWithRotation = (elements: LabelElement[]) => {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  elements.forEach((el) => {
    const angle = (el.rotation || 0) * (Math.PI / 180);
    const cosAngle = Math.cos(angle);
    const sinAngle = Math.sin(angle);

    const corners = [
      { x: el.x, y: el.y },
      { x: el.x + el.width, y: el.y },
      { x: el.x, y: el.y + el.height },
      { x: el.x + el.width, y: el.y + el.height },
    ];

    corners.forEach((corner) => {
      const rotatedX =
        el.x + (corner.x - el.x) * cosAngle - (corner.y - el.y) * sinAngle;
      const rotatedY =
        el.y + (corner.x - el.x) * sinAngle + (corner.y - el.y) * cosAngle;

      if (rotatedX < minX) minX = rotatedX;
      if (rotatedY < minY) minY = rotatedY;
      if (rotatedX > maxX) maxX = rotatedX;
      if (rotatedY > maxY) maxY = rotatedY;
    });
  });

  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY,
  };
};

const MultipleSelectedElementsProperties: React.FC<
  MultipleSelectedElementsPropertiesProps
> = ({ selectedElements, onUpdateMultipleElements }) => {
  const handlePositionChange = (
    position: 'left' | 'center' | 'right' | 'top' | 'middle' | 'bottom'
  ) => {
    const boundingBox = calculateBoundingBoxWithRotation(selectedElements);
    let newElements: LabelElement[] = [];

    if (position === 'left') {
      const groupLeft = boundingBox.x;
      newElements = selectedElements.map((el) => {
        const offset = getRotationMetric(
          el.rotation,
          el.width,
          el.height,
          'left'
        );
        return { ...el, x: groupLeft + offset };
      });
    } else if (position === 'right') {
      const groupRight = boundingBox.x + boundingBox.width;
      newElements = selectedElements.map((el) => {
        const extent = getRotationMetric(
          el.rotation,
          el.width,
          el.height,
          'right'
        );
        return { ...el, x: groupRight - extent };
      });
    } else if (position === 'top') {
      const groupTop = boundingBox.y;
      newElements = selectedElements.map((el) => {
        const offset = getRotationMetric(
          el.rotation,
          el.width,
          el.height,
          'top'
        );
        return { ...el, y: groupTop + offset };
      });
    } else if (position === 'bottom') {
      const groupBottom = boundingBox.y + boundingBox.height;
      newElements = selectedElements.map((el) => {
        const extent = getRotationMetric(
          el.rotation,
          el.width,
          el.height,
          'bottom'
        );
        return { ...el, y: groupBottom - extent };
      });
    } else if (position === 'center') {
      const groupCenterX = boundingBox.x + boundingBox.width / 2;
      newElements = selectedElements.map((el) => ({
        ...el,
        x: groupCenterX - getRelativeCenter(el.rotation, el.width, el.height).x,
      }));
    } else {
      const groupCenterY = boundingBox.y + boundingBox.height / 2;
      newElements = selectedElements.map((el) => ({
        ...el,
        y: groupCenterY - getRelativeCenter(el.rotation, el.width, el.height).y,
      }));
    }
    onUpdateMultipleElements(newElements);
  };

  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Position
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        <Typography variant="subtitle2">Horizontal</Typography>
        <ButtonGroup
          aria-label="Horizontal position"
          color="secondary"
          fullWidth
          variant="outlined"
        >
          <FBOButton
            aria-label="Left Align"
            color="neutral"
            size="small"
            variant="secondary"
            data-qa="left-align"
            onClick={() => handlePositionChange('left')}
          >
            <AlignHorizontalLeft fontSize="small" />
          </FBOButton>
          <FBOButton
            aria-label="Center Align"
            color="neutral"
            size="small"
            variant="secondary"
            data-qa="center-align"
            onClick={() => handlePositionChange('center')}
          >
            <AlignHorizontalCenter fontSize="small" />
          </FBOButton>
          <FBOButton
            aria-label="Right Align"
            color="neutral"
            size="small"
            variant="secondary"
            data-qa="right-align"
            onClick={() => handlePositionChange('right')}
          >
            <AlignHorizontalRight fontSize="small" />
          </FBOButton>
        </ButtonGroup>

        <Typography variant="subtitle2" sx={{ mt: 1 }}>
          Vertical
        </Typography>
        <ButtonGroup
          aria-label="Vertical position"
          color="secondary"
          fullWidth
          variant="outlined"
        >
          <FBOButton
            aria-label="Top Align"
            color="neutral"
            size="small"
            variant="secondary"
            data-qa="top-align"
            onClick={() => handlePositionChange('top')}
          >
            <AlignVerticalTop fontSize="small" />
          </FBOButton>
          <FBOButton
            aria-label="Middle Align"
            color="neutral"
            size="small"
            variant="secondary"
            data-qa="middle-align"
            onClick={() => handlePositionChange('middle')}
          >
            <AlignVerticalCenter fontSize="small" />
          </FBOButton>
          <FBOButton
            aria-label="Bottom Align"
            color="neutral"
            size="small"
            variant="secondary"
            data-qa="bottom-align"
            onClick={() => handlePositionChange('bottom')}
          >
            <AlignVerticalBottom fontSize="small" />
          </FBOButton>
        </ButtonGroup>
      </Box>
    </>
  );
};

export default MultipleSelectedElementsProperties;
